import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Layers, Home, Calendar, CalendarClock, ClipboardCheck, Building2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { getLotes, createLote, updateLote, deleteLote, getNaves, createNave, updateNave, deleteNave, getEmpresaConfig, updateEmpresaConfig } from '../lib/api';
import { formatDate } from '../lib/dateUtils';

const initialLoteForm = { numero_lote: '', fecha_ingreso: '', cantidad_gallinas: '', edad_semanas: 0, fase: 'Levante', estado: 'Activo' };
const initialNaveForm = { nombre_nave: '', lote_id: '', cantidad_inicial_gallinas: '', fecha_ingreso: '' };

export default function Configuracion() {
  const [lotes, setLotes] = useState([]);
  const [naves, setNaves] = useState([]);
  const [loading, setLoading] = useState(true);

  // Lote dialog
  const [loteDialogOpen, setLoteDialogOpen] = useState(false);
  const [loteForm, setLoteForm] = useState(initialLoteForm);
  const [editingLoteId, setEditingLoteId] = useState(null);
  const [deletingLoteId, setDeletingLoteId] = useState(null);

  // Nave dialog
  const [naveDialogOpen, setNaveDialogOpen] = useState(false);
  const [naveForm, setNaveForm] = useState(initialNaveForm);
  const [editingNaveId, setEditingNaveId] = useState(null);
  const [deletingNaveId, setDeletingNaveId] = useState(null);
  const [savingLote, setSavingLote] = useState(false);
  const [savingNave, setSavingNave] = useState(false);

  // Fecha de referencia para edad
  const [fechaEdadDialogOpen, setFechaEdadDialogOpen] = useState(false);
  const [loteParaFechaEdad, setLoteParaFechaEdad] = useState(null);
  const [savingFechaEdad, setSavingFechaEdad] = useState(false);

  // Fecha de edad POR NAVE (configuración rápida desde la columna Acciones)
  const [fechaEdadNaveDialogOpen, setFechaEdadNaveDialogOpen] = useState(false);
  const [naveParaFechaEdad, setNaveParaFechaEdad] = useState(null);
  const [fechaEdadNaveInput, setFechaEdadNaveInput] = useState('');
  const [savingFechaEdadNave, setSavingFechaEdadNave] = useState(false);

  // Fecha desde la cual VERIFICAR REGISTROS FALTANTES por nave.
  // Si está vacío, el sistema usa el primer registro de producción.
  const [fechaControlDialogOpen, setFechaControlDialogOpen] = useState(false);
  const [naveParaFechaControl, setNaveParaFechaControl] = useState(null);
  const [fechaControlInput, setFechaControlInput] = useState('');
  const [savingFechaControl, setSavingFechaControl] = useState(false);

  // Configuración de Empresa (RNC, nombre, dirección, teléfono)
  const [empresaForm, setEmpresaForm] = useState({ rnc: '', nombre: '', direccion: '', telefono: '' });
  const [savingEmpresa, setSavingEmpresa] = useState(false);

  useEffect(() => {
    getEmpresaConfig()
      .then((res) => setEmpresaForm(res.data))
      .catch(() => { /* default ya en state */ });
  }, []);

  const saveEmpresa = async () => {
    if (!empresaForm.rnc?.trim()) {
      toast.error('El RNC es obligatorio');
      return;
    }
    setSavingEmpresa(true);
    try {
      const res = await updateEmpresaConfig(empresaForm);
      setEmpresaForm(res.data);
      toast.success('Datos de empresa guardados');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error guardando datos');
    } finally {
      setSavingEmpresa(false);
    }
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [lotesRes, navesRes] = await Promise.all([getLotes(), getNaves()]);
      setLotes(lotesRes.data);
      setNaves(navesRes.data);
    } catch (err) {
      toast.error('Error cargando datos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ---- Lote handlers ----
  const openNewLote = () => {
    setLoteForm(initialLoteForm);
    setEditingLoteId(null);
    setLoteDialogOpen(true);
  };

  const openEditLote = (lote) => {
    setLoteForm({
      numero_lote: lote.numero_lote,
      fecha_ingreso: lote.fecha_ingreso?.split('T')[0] || '',
      cantidad_gallinas: lote.cantidad_gallinas?.toString() || '',
      edad_semanas: lote.edad_semanas || 0,
      fase: lote.fase || 'Levante',
      estado: lote.estado,
    });
    setEditingLoteId(lote._id || lote.id);
    setLoteDialogOpen(true);
  };

  const saveLote = async () => {
    if (!loteForm.numero_lote || !loteForm.fecha_ingreso || !loteForm.cantidad_gallinas) {
      toast.error('Complete todos los campos requeridos');
      return;
    }
    setSavingLote(true);
    try {
      const payload = {
        numero_lote: loteForm.numero_lote,
        fecha_ingreso: loteForm.fecha_ingreso,
        cantidad_gallinas: parseInt(loteForm.cantidad_gallinas),
        edad_semanas: parseInt(loteForm.edad_semanas) || 0,
        fase: loteForm.fase,
        estado: loteForm.estado
      };
      
      if (editingLoteId) {
        await updateLote(editingLoteId, payload);
        toast.success('Lote actualizado');
      } else {
        await createLote(payload);
        toast.success('Lote creado exitosamente');
      }
      setLoteDialogOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error guardando lote');
    } finally {
      setSavingLote(false);
    }
  };

  const confirmDeleteLote = async () => {
    try {
      await deleteLote(deletingLoteId);
      toast.success('Lote eliminado');
      setDeletingLoteId(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error eliminando lote');
    }
  };

  // ---- Nave handlers ----
  const openNewNave = () => {
    setNaveForm(initialNaveForm);
    setEditingNaveId(null);
    setNaveDialogOpen(true);
  };

  const openEditNave = (nave) => {
    setNaveForm({
      nombre_nave: nave.nombre_nave,
      lote_id: nave.lote_id,
      cantidad_inicial_gallinas: String(nave.cantidad_inicial_gallinas),
      fecha_ingreso: nave.fecha_ingreso?.split('T')[0] || '',
    });
    setEditingNaveId(nave.id || nave._id);
    setNaveDialogOpen(true);
  };

  const saveNave = async () => {
    if (!naveForm.nombre_nave || !naveForm.lote_id || !naveForm.cantidad_inicial_gallinas || !naveForm.fecha_ingreso) {
      toast.error('Complete todos los campos requeridos');
      return;
    }
    setSavingNave(true);
    try {
      const data = { ...naveForm, cantidad_inicial_gallinas: parseInt(naveForm.cantidad_inicial_gallinas) };
      if (editingNaveId) {
        await updateNave(editingNaveId, data);
        toast.success('Nave actualizada');
      } else {
        await createNave(data);
        toast.success('Nave creada exitosamente');
      }
      setNaveDialogOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error guardando nave');
    } finally {
      setSavingNave(false);
    }
  };

  const confirmDeleteNave = async () => {
    try {
      await deleteNave(deletingNaveId);
      toast.success('Nave eliminada');
      setDeletingNaveId(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error eliminando nave');
    }
  };

  // Configurar fecha de edad rápido para una nave individual
  const openFechaEdadNave = (nave) => {
    setNaveParaFechaEdad(nave);
    setFechaEdadNaveInput(nave.fecha_ingreso?.split('T')[0] || '');
    setFechaEdadNaveDialogOpen(true);
  };

  const saveFechaEdadNave = async () => {
    if (!naveParaFechaEdad) return;
    if (!fechaEdadNaveInput) {
      toast.error('Seleccione una fecha');
      return;
    }
    setSavingFechaEdadNave(true);
    try {
      await updateNave(naveParaFechaEdad.id || naveParaFechaEdad._id, {
        fecha_ingreso: fechaEdadNaveInput,
      });
      toast.success(`Fecha de edad actualizada · ${naveParaFechaEdad.nombre_nave}`);
      setFechaEdadNaveDialogOpen(false);
      setNaveParaFechaEdad(null);
      setFechaEdadNaveInput('');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error actualizando fecha');
    } finally {
      setSavingFechaEdadNave(false);
    }
  };

  // ---- Fecha de control de registros faltantes (override por nave) ----
  const openFechaControlNave = (nave) => {
    setNaveParaFechaControl(nave);
    setFechaControlInput(nave.fecha_control_registros?.split('T')[0] || '');
    setFechaControlDialogOpen(true);
  };

  const saveFechaControlNave = async (clear = false) => {
    if (!naveParaFechaControl) return;
    if (!clear && !fechaControlInput) {
      toast.error('Seleccione una fecha o pulse "Quitar override"');
      return;
    }
    setSavingFechaControl(true);
    try {
      await updateNave(naveParaFechaControl.id || naveParaFechaControl._id, {
        // "" → el backend lo interpreta como NULL (limpiar override)
        fecha_control_registros: clear ? '' : fechaControlInput,
      });
      toast.success(
        clear
          ? `Override quitado · ${naveParaFechaControl.nombre_nave}`
          : `Verificación desde ${fechaControlInput} · ${naveParaFechaControl.nombre_nave}`,
      );
      setFechaControlDialogOpen(false);
      setNaveParaFechaControl(null);
      setFechaControlInput('');
      loadData();
    } catch (err) {
      const msg = err.response?.data?.detail || '';
      if (msg.toLowerCase().includes('fecha_control_registros') || msg.toLowerCase().includes('column')) {
        toast.error('Falta agregar la columna en Supabase. Ver instrucciones del agente.');
      } else {
        toast.error(msg || 'Error actualizando fecha');
      }
    } finally {
      setSavingFechaControl(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Tabs defaultValue="lotes">
        <TabsList className="mb-4">
          <TabsTrigger value="lotes" className="gap-2">
            <Layers className="w-4 h-4" /> Lotes
          </TabsTrigger>
          <TabsTrigger value="naves" className="gap-2">
            <Home className="w-4 h-4" /> Naves
          </TabsTrigger>
          <TabsTrigger value="empresa" className="gap-2" data-testid="tab-empresa">
            <Building2 className="w-4 h-4" /> Empresa
          </TabsTrigger>
        </TabsList>

        {/* LOTES TAB */}
        <TabsContent value="lotes">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk' }}>Gestión de Lotes</CardTitle>
              <Button onClick={openNewLote} size="sm" data-testid="btn-nuevo-lote">
                <Plus className="w-4 h-4 mr-2" /> Nuevo Lote
              </Button>
            </CardHeader>
            <CardContent>
              {lotes.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Layers className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No hay lotes registrados</p>
                  <p className="text-sm">Comience creando su primer lote</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table data-testid="tabla-lotes">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Número de Lote</TableHead>
                        <TableHead>Fecha Ingreso</TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead>Fase</TableHead>
                        <TableHead>Naves</TableHead>
                        <TableHead className="text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {lotes.map((lote) => {
                        const loteId = lote.id || lote._id;
                        const naveCount = naves.filter(n => n.lote_id === loteId).length;
                        return (
                          <TableRow key={loteId} className="hover:bg-accent/60 transition-colors duration-150">
                            <TableCell className="font-medium">{lote.numero_lote}</TableCell>
                            <TableCell>{formatDate(lote.fecha_ingreso)}</TableCell>
                            <TableCell>
                              <Badge variant={lote.estado === 'Activo' ? 'default' : 'secondary'}>
                                {lote.estado}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{lote.fase || 'Levante'}</Badge>
                            </TableCell>
                            <TableCell>{naveCount}</TableCell>
                            <TableCell className="text-right">
                              <Button variant="ghost" size="icon" onClick={() => openEditLote(lote)} data-testid="btn-edit-lote">
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                onClick={() => {
                                  setLoteParaFechaEdad(lote);
                                  setFechaEdadDialogOpen(true);
                                }}
                                title="Configurar fecha de edad"
                              >
                                <Calendar className="w-4 h-4 text-blue-600" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => setDeletingLoteId(loteId)}>
                                <Trash2 className="w-4 h-4 text-destructive" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* NAVES TAB */}
        <TabsContent value="naves">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk' }}>Gestión de Naves</CardTitle>
              <Button onClick={openNewNave} size="sm" data-testid="btn-nueva-nave">
                <Plus className="w-4 h-4 mr-2" /> Nueva Nave
              </Button>
            </CardHeader>
            <CardContent>
              {naves.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Home className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No hay naves registradas</p>
                  <p className="text-sm">Cree un lote primero y luego añada naves</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table data-testid="tabla-naves">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nombre</TableHead>
                        <TableHead>Lote</TableHead>
                        <TableHead className="text-right">Gallinas Iniciales</TableHead>
                        <TableHead className="text-right">Gallinas Actuales</TableHead>
                        <TableHead className="text-center">Semanas de Edad</TableHead>
                        <TableHead>Fecha Ingreso</TableHead>
                        <TableHead className="text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {naves.map((nave) => (
                        <TableRow key={nave.id || nave._id} className="hover:bg-accent/60 transition-colors duration-150">
                          <TableCell className="font-medium">{nave.nombre_nave}</TableCell>
                          <TableCell>{nave.lote_nombre || 'N/A'}</TableCell>
                          <TableCell className="text-right tabular-nums">{nave.cantidad_inicial_gallinas?.toLocaleString('es-ES')}</TableCell>
                          <TableCell className="text-right tabular-nums font-semibold">{nave.gallinas_actuales?.toLocaleString('es-ES')}</TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline" className="tabular-nums">
                              <Calendar className="w-3 h-3 mr-1" />
                              {nave.semanas_edad ?? 0} sem.
                            </Badge>
                          </TableCell>
                          <TableCell>{nave.fecha_ingreso ? formatDate(nave.fecha_ingreso) : '-'}</TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openFechaEdadNave(nave)}
                              title="Configurar fecha de edad de esta nave"
                              data-testid={`btn-fecha-edad-nave-${nave.id || nave._id}`}
                            >
                              <CalendarClock className="w-4 h-4 text-primary" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openFechaControlNave(nave)}
                              title={
                                nave.fecha_control_registros
                                  ? `Verificar registros desde: ${formatDate(nave.fecha_control_registros)}`
                                  : 'Verificar registros faltantes desde una fecha específica'
                              }
                              data-testid={`btn-fecha-control-nave-${nave.id || nave._id}`}
                              className={nave.fecha_control_registros ? 'relative' : ''}
                            >
                              <ClipboardCheck className={`w-4 h-4 ${nave.fecha_control_registros ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'}`} />
                              {nave.fecha_control_registros && (
                                <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-amber-500" />
                              )}
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => openEditNave(nave)} title="Editar nave">
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => setDeletingNaveId(nave.id || nave._id)} title="Eliminar nave">
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* EMPRESA TAB */}
        <TabsContent value="empresa">
          <Card data-testid="card-empresa-config">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
                <Building2 className="w-5 h-5" /> Datos de la Empresa
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Esta información aparece en la cabecera de cada conduce impreso o exportado a PDF.
              </p>
            </CardHeader>
            <CardContent className="space-y-4 max-w-2xl">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="emp-rnc">RNC *</Label>
                  <Input
                    id="emp-rnc"
                    value={empresaForm.rnc || ''}
                    onChange={(e) => setEmpresaForm({ ...empresaForm, rnc: e.target.value })}
                    placeholder="131-84645-9"
                    className="h-11 mt-1.5 tabular-nums"
                    data-testid="input-empresa-rnc"
                  />
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Registro Nacional del Contribuyente. Aparece debajo del N° del conduce.
                  </p>
                </div>
                <div>
                  <Label htmlFor="emp-nombre">Nombre comercial</Label>
                  <Input
                    id="emp-nombre"
                    value={empresaForm.nombre || ''}
                    onChange={(e) => setEmpresaForm({ ...empresaForm, nombre: e.target.value })}
                    placeholder="AVIPORK"
                    className="h-11 mt-1.5"
                    data-testid="input-empresa-nombre"
                  />
                </div>
                <div>
                  <Label htmlFor="emp-direccion">Dirección</Label>
                  <Input
                    id="emp-direccion"
                    value={empresaForm.direccion || ''}
                    onChange={(e) => setEmpresaForm({ ...empresaForm, direccion: e.target.value })}
                    placeholder="Dirección de la empresa"
                    className="h-11 mt-1.5"
                    data-testid="input-empresa-direccion"
                  />
                </div>
                <div>
                  <Label htmlFor="emp-telefono">Teléfono</Label>
                  <Input
                    id="emp-telefono"
                    value={empresaForm.telefono || ''}
                    onChange={(e) => setEmpresaForm({ ...empresaForm, telefono: e.target.value })}
                    placeholder="(809) 000-0000"
                    className="h-11 mt-1.5"
                    data-testid="input-empresa-telefono"
                  />
                </div>
              </div>
              <div className="pt-2">
                <Button onClick={saveEmpresa} disabled={savingEmpresa} data-testid="btn-guardar-empresa">
                  {savingEmpresa ? 'Guardando...' : 'Guardar Datos de Empresa'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* LOTE DIALOG */}
      <Dialog open={loteDialogOpen} onOpenChange={setLoteDialogOpen}>
        <DialogContent data-testid="modal-lote">
          <DialogHeader>
            <DialogTitle>{editingLoteId ? 'Editar Lote' : 'Nuevo Lote'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="numero_lote">Número de Lote *</Label>
              <Input
                id="numero_lote"
                placeholder="Ej: LOTE-001"
                value={loteForm.numero_lote}
                onChange={(e) => setLoteForm({ ...loteForm, numero_lote: e.target.value })}
                className="h-11 mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="fecha_ingreso_lote">Fecha de Ingreso *</Label>
              <Input
                id="fecha_ingreso_lote"
                type="date"
                value={loteForm.fecha_ingreso}
                onChange={(e) => setLoteForm({ ...loteForm, fecha_ingreso: e.target.value })}
                className="h-11 mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="cantidad_gallinas">Cantidad de Gallinas *</Label>
              <Input
                id="cantidad_gallinas"
                type="number"
                placeholder="Ej: 1000"
                value={loteForm.cantidad_gallinas}
                onChange={(e) => setLoteForm({ ...loteForm, cantidad_gallinas: e.target.value })}
                className="h-11 mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="edad_semanas">Edad en Semanas</Label>
              <Input
                id="edad_semanas"
                type="number"
                placeholder="0"
                value={loteForm.edad_semanas}
                onChange={(e) => setLoteForm({ ...loteForm, edad_semanas: e.target.value })}
                className="h-11 mt-1.5"
              />
            </div>
            <div>
              <Label>Fase</Label>
              <Select value={loteForm.fase} onValueChange={(v) => setLoteForm({ ...loteForm, fase: v })}>
                <SelectTrigger className="h-11 mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Levante">Levante</SelectItem>
                  <SelectItem value="Postura">Postura</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Estado</Label>
              <Select value={loteForm.estado} onValueChange={(v) => setLoteForm({ ...loteForm, estado: v })}>
                <SelectTrigger className="h-11 mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Activo">Activo</SelectItem>
                  <SelectItem value="Inactivo">Inactivo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLoteDialogOpen(false)}>Cancelar</Button>
            <Button onClick={saveLote} disabled={savingLote} data-testid="btn-guardar-lote">
              {savingLote ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* NAVE DIALOG */}
      <Dialog open={naveDialogOpen} onOpenChange={setNaveDialogOpen}>
        <DialogContent data-testid="modal-nave">
          <DialogHeader>
            <DialogTitle>{editingNaveId ? 'Editar Nave' : 'Nueva Nave'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="nombre_nave">Nombre de Nave *</Label>
              <Input
                id="nombre_nave"
                placeholder="Ej: Nave Alpha"
                value={naveForm.nombre_nave}
                onChange={(e) => setNaveForm({ ...naveForm, nombre_nave: e.target.value })}
                className="h-11 mt-1.5"
              />
            </div>
            <div>
              <Label>Lote Asignado *</Label>
              <Select value={naveForm.lote_id} onValueChange={(v) => setNaveForm({ ...naveForm, lote_id: v })}>
                <SelectTrigger className="h-11 mt-1.5">
                  <SelectValue placeholder="Seleccione un lote" />
                </SelectTrigger>
                <SelectContent>
                  {lotes.filter(l => l.estado === 'Activo').map(l => {
                    const loteId = l.id || l._id;
                    return (
                      <SelectItem key={loteId} value={loteId}>{l.numero_lote}</SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="cant_gallinas">Cantidad Inicial de Gallinas *</Label>
              <Input
                id="cant_gallinas"
                type="number"
                inputMode="numeric"
                placeholder="Ej: 1000"
                value={naveForm.cantidad_inicial_gallinas}
                onChange={(e) => setNaveForm({ ...naveForm, cantidad_inicial_gallinas: e.target.value })}
                className="h-11 mt-1.5"
              />
              <p className="text-xs text-muted-foreground mt-1">aves</p>
            </div>
            <div>
              <Label htmlFor="fecha_ingreso_nave" className="flex items-center gap-2">
                Fecha de Ingreso / Referencia de Edad *
              </Label>
              <Input
                id="fecha_ingreso_nave"
                type="date"
                value={naveForm.fecha_ingreso}
                onChange={(e) => setNaveForm({ ...naveForm, fecha_ingreso: e.target.value })}
                className="h-11 mt-1.5"
                data-testid="input-fecha-ingreso-nave"
              />
              <div className="mt-1.5 flex items-start gap-1.5 text-xs text-muted-foreground bg-primary/5 border border-primary/10 rounded-md px-2.5 py-2">
                <Calendar className="w-3.5 h-3.5 mt-0.5 text-primary shrink-0" />
                <p>
                  Esta fecha se usa para calcular las <strong>semanas de edad</strong> de las
                  gallinas de <em>esta nave específica</em> — puede ser diferente a la de otras
                  naves del mismo lote. Si la cambia, la edad de esta nave se recalcula al instante.
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNaveDialogOpen(false)}>Cancelar</Button>
            <Button onClick={saveNave} disabled={savingNave} data-testid="btn-guardar-nave">
              {savingNave ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DELETE LOTE CONFIRM */}
      <AlertDialog open={!!deletingLoteId} onOpenChange={() => setDeletingLoteId(null)}>
        <AlertDialogContent data-testid="confirmar-eliminar">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este lote?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer. Solo se pueden eliminar lotes sin naves asignadas.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteLote} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* DELETE NAVE CONFIRM */}
      <AlertDialog open={!!deletingNaveId} onOpenChange={() => setDeletingNaveId(null)}>
        <AlertDialogContent data-testid="confirmar-eliminar">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar esta nave?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer. Solo se pueden eliminar naves sin registros diarios.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteNave} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog: Configurar Fecha de Edad POR NAVE (rápido desde Acciones) */}
      <Dialog
        open={fechaEdadNaveDialogOpen}
        onOpenChange={(o) => {
          if (!o) { setFechaEdadNaveDialogOpen(false); setNaveParaFechaEdad(null); setFechaEdadNaveInput(''); }
        }}
      >
        <DialogContent className="sm:max-w-[480px]" data-testid="modal-fecha-edad-nave">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
              <CalendarClock className="w-5 h-5 text-primary" />
              Configurar Fecha de Edad
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {naveParaFechaEdad && (
              <div className="bg-accent rounded-lg px-3 py-2.5 flex items-center gap-3">
                <Home className="w-4 h-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground">Nave seleccionada</p>
                  <p className="font-semibold text-sm" style={{ fontFamily: 'Space Grotesk' }}>
                    {naveParaFechaEdad.nombre_nave}
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    Lote: {naveParaFechaEdad.lote_nombre || 'N/A'} · Edad actual:{' '}
                    <strong className="text-foreground">{naveParaFechaEdad.semanas_edad ?? 0} sem.</strong>
                  </p>
                </div>
              </div>
            )}
            <div>
              <Label htmlFor="fecha-edad-nave-input">Fecha de Referencia *</Label>
              <Input
                id="fecha-edad-nave-input"
                type="date"
                value={fechaEdadNaveInput}
                onChange={(e) => setFechaEdadNaveInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !savingFechaEdadNave) saveFechaEdadNave(); }}
                className="h-11 mt-1.5"
                autoFocus
                data-testid="input-fecha-edad-nave"
              />
              <p className="text-xs text-muted-foreground mt-1.5">
                Las gallinas de <strong>esta nave</strong> tendrán su edad calculada desde esta fecha.
                Cambiar este valor no afecta las otras naves del mismo lote.
              </p>
            </div>
            {fechaEdadNaveInput && (() => {
              const hoy = new Date();
              const [y, m, d] = fechaEdadNaveInput.split('-').map(Number);
              if (y && m && d) {
                const f = new Date(y, m - 1, d);
                // Anclar ambas al lunes de su semana
                const toMonday = (dt) => {
                  const dow = dt.getDay();
                  const offset = dow === 0 ? 6 : dow - 1;
                  return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate() - offset);
                };
                const diffDays = Math.round((toMonday(hoy) - toMonday(f)) / (1000 * 60 * 60 * 24));
                const semanas = Math.max(0, Math.floor(diffDays / 7));
                return (
                  <div className="bg-primary/10 border border-primary/20 rounded-lg px-3 py-2.5 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-primary" />
                    <div>
                      <p className="text-[11px] text-muted-foreground">Nueva edad calculada</p>
                      <p className="text-lg font-bold tabular-nums" style={{ fontFamily: 'Space Grotesk' }}>
                        {semanas} <span className="text-sm font-medium text-muted-foreground">semanas</span>
                      </p>
                    </div>
                  </div>
                );
              }
              return null;
            })()}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setFechaEdadNaveDialogOpen(false); setNaveParaFechaEdad(null); setFechaEdadNaveInput(''); }}
              disabled={savingFechaEdadNave}
            >
              Cancelar
            </Button>
            <Button
              onClick={saveFechaEdadNave}
              disabled={savingFechaEdadNave || !fechaEdadNaveInput}
              data-testid="btn-guardar-fecha-edad-nave"
            >
              {savingFechaEdadNave ? 'Guardando...' : 'Guardar fecha'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Verificar Registros Faltantes desde una fecha (override por nave) */}
      <Dialog
        open={fechaControlDialogOpen}
        onOpenChange={(o) => {
          if (!o) { setFechaControlDialogOpen(false); setNaveParaFechaControl(null); setFechaControlInput(''); }
        }}
      >
        <DialogContent className="sm:max-w-[480px]" data-testid="modal-fecha-control">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
              <ClipboardCheck className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              Verificar Registros Faltantes Desde…
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {naveParaFechaControl && (
              <div className="bg-accent rounded-lg px-3 py-2.5 flex items-center gap-3">
                <Home className="w-4 h-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground">Nave seleccionada</p>
                  <p className="font-semibold text-sm" style={{ fontFamily: 'Space Grotesk' }}>
                    {naveParaFechaControl.nombre_nave}
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    Lote: {naveParaFechaControl.lote_nombre || 'N/A'}
                    {naveParaFechaControl.fecha_control_registros && (
                      <> · Override actual: <strong className="text-amber-600 dark:text-amber-400">{formatDate(naveParaFechaControl.fecha_control_registros)}</strong></>
                    )}
                  </p>
                </div>
              </div>
            )}
            <div>
              <Label htmlFor="fecha-control-input">Fecha desde la cual verificar</Label>
              <Input
                id="fecha-control-input"
                type="date"
                value={fechaControlInput}
                onChange={(e) => setFechaControlInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !savingFechaControl) saveFechaControlNave(false); }}
                className="h-11 mt-1.5"
                autoFocus
                data-testid="input-fecha-control"
              />
              <p className="text-xs text-muted-foreground mt-1.5">
                A partir de esta fecha el sistema buscará registros diarios faltantes
                <strong> solo para esta nave</strong>. Si lo dejas sin valor, se usará
                el <strong>primer registro de producción</strong> de la nave (comportamiento por defecto).
              </p>
            </div>
            <div className="rounded-md border border-amber-200/60 dark:border-amber-900/40 bg-amber-50/60 dark:bg-amber-950/20 px-3 py-2 text-xs text-amber-900 dark:text-amber-100">
              <strong>Tip:</strong> útil cuando la nave tiene muchos huecos antiguos que
              no quieres reportar (por ejemplo, registros previos a una recría).
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            {naveParaFechaControl?.fecha_control_registros && (
              <Button
                variant="outline"
                onClick={() => saveFechaControlNave(true)}
                disabled={savingFechaControl}
                className="sm:mr-auto"
                data-testid="btn-quitar-fecha-control"
              >
                Quitar override
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={() => { setFechaControlDialogOpen(false); setNaveParaFechaControl(null); setFechaControlInput(''); }}
              disabled={savingFechaControl}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => saveFechaControlNave(false)}
              disabled={savingFechaControl || !fechaControlInput}
              data-testid="btn-guardar-fecha-control"
            >
              {savingFechaControl ? 'Guardando...' : 'Guardar fecha'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Configurar Fecha de Edad */}
      <Dialog open={fechaEdadDialogOpen} onOpenChange={setFechaEdadDialogOpen}>
        <DialogContent className="sm:max-w-[500px]" data-testid="modal-fecha-edad">
          <DialogHeader>
            <DialogTitle>Configurar Fecha de Referencia de Edad</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              La edad de las gallinas se calculará desde esta fecha. Si no se configura, se usará la fecha de ingreso del lote.
            </p>
            <div className="space-y-2">
              <Label htmlFor="fecha_ref_edad">Fecha de Referencia</Label>
              <Input
                id="fecha_ref_edad"
                type="date"
                value={loteParaFechaEdad?.fecha_referencia_edad?.split('T')[0] || loteParaFechaEdad?.fecha_ingreso?.split('T')[0] || ''}
                onChange={(e) => setLoteParaFechaEdad({ ...loteParaFechaEdad, fecha_referencia_edad: e.target.value })}
                className="h-11"
                data-testid="input-fecha-referencia"
              />
              <p className="text-xs text-muted-foreground">
                Lote: <span className="font-medium">{loteParaFechaEdad?.numero_lote}</span>
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFechaEdadDialogOpen(false)} disabled={savingFechaEdad}>
              Cancelar
            </Button>
            <Button
              disabled={savingFechaEdad}
              data-testid="btn-guardar-fecha-edad"
              onClick={async () => {
                const fecha = loteParaFechaEdad?.fecha_referencia_edad;
                if (!fecha) {
                  toast.error('Seleccione una fecha');
                  return;
                }
                setSavingFechaEdad(true);
                try {
                  await updateLote(loteParaFechaEdad.id || loteParaFechaEdad._id, {
                    fecha_referencia_edad: fecha,
                  });
                  toast.success('Fecha de referencia actualizada');
                  setFechaEdadDialogOpen(false);
                  setLoteParaFechaEdad(null);
                  loadData();
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Error al actualizar fecha');
                } finally {
                  setSavingFechaEdad(false);
                }
              }}
            >
              {savingFechaEdad ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
